import { analyzeDatasetUploadAssets, type DatasetUploadAssets } from "@/modules/dataset/public";
import type { ChangeEvent, DragEvent, KeyboardEvent, SyntheticEvent } from "react";
import type { Lifecycle } from "@/lifecycle";
import {
  cancelDatasetUpload,
  listAllEmbodiments,
  listDatasetUploadLocations,
  listDatasetUploads,
  listDatasetUploadStagedFiles,
  prefillBandDiagnosticsEnabled,
  prefillReservedLabels,
  uploadJobSupportsBandDiagnostics,
  type DataOrigin,
  type DatasetUploadJob,
  type DatasetUploadLocation,
  type Embodiment,
  type Tip,
  type UploadReservedLabels,
} from "@/api";
import {
  findMissingRequiredKeys,
  reservedKeyLabel,
  type ReservedSelections,
} from "@/components/label";
import {
  diffStagedFiles,
  fetchAllStagedFiles,
  isForeignLiveLease,
  listResumableUploads,
  probeFolderReadability,
  scanDroppedItems,
  scanFileList,
  type FolderReadFailure,
  type FolderScanResult,
  type UploadJobSpec,
} from "@/upload";
import { UPLOAD_FORM_DEFAULTS } from "./uploadFormDefaults";

/**
 * 업로드 창의 설정 단계 컨트롤러다. 공용 Dataset 업로드(files 모드)를 다룬다.
 *
 * Owns:
 * - 설정 폼 상태(폴더 스캔·이름·자동 완료·검증)와 재개/재업로드 프롬프트.
 * - 예약 라벨·embodiment·tip·행동 주체 필드와 필수 입력 검증.
 * - "시작" 을 누르면 UploadJobSpec 을 만들어 enqueue 한다. 그 뒤 엔진 잡 id 를 onSubmitted 로
 *   창에 전달한다. 창은 진행 단계로 전환한다.
 *
 * Does not own:
 * - 업로드 실행·진행·완료. 앱 스코프 uploadEngine 이 소유한다.
 * - 창 lifecycle. uploadWindows 가 소유한다.
 *
 * new/retry(reset)/resume 세 진입을 지원한다.
 */

export type UploadDialogMode = "new" | "retry" | "resume";

export type UploadFormState = {
  scan: FolderScanResult | null;
  assets: DatasetUploadAssets | null;
  displayName: string;
  reservedSelections: ReservedSelections;
  // 선택한 하드웨어 프로필. 신규 업로드에서 null 이면 리뷰에서 확정한다.
  // reset 에서 null 이면 서버 저장값을 유지한다.
  embodiment: Embodiment | null;
  tip: Tip | null;
  // 행동 주체. embodiment 와 달리 선언 없이 시작할 수 없다. 그래서 validate 가 필수로 검사한다.
  dataOrigin: DataOrigin | null;
  // 이 반입본의 컬렉션 밴드와 큐레이션 집계를 산출할지 정한다. 에피소드 결함 검사는 항상 실행한다.
  // 기본값은 산출이다. source 를 open_source 로 고르면 자동으로 해제된다. 이것은 판정이 아니라
  // pre-fill 이므로 사용자가 다시 켤 수 있다.
  bandDiagnosticsEnabled: boolean;
  // 파일을 업로드할 서버 위치. **null 은 미지정**이고, 이때 파일은 조직 기본 위치로 간다.
  // 화면이 기본 위치를 골라 보여 주어도, 사용자가 실제로 고르기 전까지 이 값은 null 이다(rules §16).
  uploadLocationCode: string | null;
};

// resume 모드에서 폴더를 선택한 직후 계산하는 probe diff 요약. UI 미리보기에만 쓴다.
// 실행할 때 엔진이 다시 계산한다.
export type UploadResumeDiff = {
  skippedCount: number;
  skippedBytes: number;
  remainingCount: number;
  remainingBytes: number;
  mismatchedCount: number;
  orphanCount: number;
};

export type UploadDialogState = {
  mode: UploadDialogMode;
  form: UploadFormState;
  errorMessage: string | null;
  busy: boolean;
  scanning: boolean;
  dragActive: boolean;
  // new 모드에서 탐지한 미완료 업로드. 이어받기 후보 배너에 표시한다.
  resumeCandidate: DatasetUploadJob | null;
  // retry/resume 모드의 대상 잡.
  targetJob: DatasetUploadJob | null;
  resumeDiff: UploadResumeDiff | null;
  // staged 실측(probe)을 기다리는 중이다. `busy` 는 업로드 취소도 쓰므로 probe 대기 문구는 이 값으로 가른다.
  resumeProbing: boolean;
  // probe 가 실패했다. 미리보기만 없고 재개는 막지 않는다. 엔진이 시작할 때 다시 계산한다.
  resumeDiffFailed: boolean;
  // 폴더 선택 직후의 읽기 확인 결과. null = 이상 없음.
  readFailure: FolderReadFailure | null;
  // 읽기 확인이 진행 중이다. 판정 전에는 시작을 막는다.
  checkingRead: boolean;
};

// Embodiment 카탈로그의 조회 상태. 받기 전과 실패는 후보가 비어 있다. 빈 카탈로그와 구분한다.
export type EmbodimentCatalogStatus = "loading" | "ready" | "error";

export type UploadDialogSnapshot = {
  dialog: UploadDialogState;
  // Embodiment 선택 드롭다운의 후보. 창을 열 때 한 번 받고 mode 가 바뀌어도 유지한다.
  // 이 값은 폼 상태가 아니라 카탈로그이므로 createDialog 가 바꾸지 않는다. 로드가 실패하면 빈 목록이다.
  embodiments: readonly Embodiment[];
  embodimentCatalog: EmbodimentCatalogStatus;
  // 고를 수 있는 업로드 서버 위치. embodiments 와 같이 카탈로그다. 로드가 실패하면 빈 목록이다.
  // 그때 폼은 위치 필드를 감춘다. 고를 위치가 없어도 업로드는 막지 않는다.
  uploadLocations: readonly DatasetUploadLocation[];
};

type UploadWindowControllerOptions = {
  folderInputId: string;
  // 작업을 앱 스코프 엔진에 전달한다(보통 uploadEngine.enqueue). 만든 엔진 잡 id 를 돌려준다.
  enqueue: (spec: UploadJobSpec) => string;
  resolveErrorMessage: (error: unknown) => string;
  // enqueue 직후 호출한다. 창은 이 호출로 진행 단계로 전환한다(엔진 잡 id, 표시 이름).
  onSubmitted: (engineJobId: string, displayName: string) => void;
  // 재개 취소 등으로 서버 잡이 바뀐 뒤 목록을 갱신한다. 선택 항목이다.
  onJobsRefresh?: () => void | Promise<void>;
};

// 재개 탐지에서 훑을 최근 업로드 잡 수. 모달 열 때 한 번만 조회한다.
const resumeScanPageSize = 20;

const emptyForm: UploadFormState = {
  scan: null,
  assets: null,
  displayName: "",
  reservedSelections: {},
  embodiment: null,
  tip: null,
  dataOrigin: null,
  bandDiagnosticsEnabled: true,
  uploadLocationCode: null,
};

export class UploadWindowController implements Lifecycle<
  readonly [seed?: { kind: "resume" | "retry"; job: DatasetUploadJob }]
> {
  private readonly listeners = new Set<() => void>();
  private readonly options: UploadWindowControllerOptions;
  private snapshot: UploadDialogSnapshot;
  private disposed = true;
  private lifecycleGeneration = 0;

  // 업로드 시점에 강제할 reserved key. contract 로딩 후 소비처가 설정한다.
  reservedRequiredKeys: readonly string[] = [];

  constructor(options: UploadWindowControllerOptions) {
    this.options = options;
    this.snapshot = {
      dialog: createNewDialog(),
      embodiments: [],
      embodimentCatalog: "loading",
      uploadLocations: [],
    };
  }

  getSnapshot = (): UploadDialogSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  // 콘텐츠 mount 직후 1회 호출한다(idempotent). seed 가 있으면 재개 또는 재업로드 폼을 연다.
  // seed 가 없으면 신규 폼을 열고 재개 후보를 탐지한다. seed 타입은 uploadWindow 의 UploadSeed 와
  // 구조가 같다. 순환 import 를 피하려고 타입을 따로 둔다.
  init = (seed?: { kind: "resume" | "retry"; job: DatasetUploadJob }): void => {
    if (!this.disposed) {
      return;
    }
    this.disposed = false;
    // 두 카탈로그는 서로 독립이므로 병렬로 요청한다. 직렬로 요청하면 창을 열 때 왕복이 두 번 생긴다.
    void this.loadEmbodiments();
    void this.loadUploadLocations();
    if (seed === undefined) {
      this.setDialog(createNewDialog());
      void this.detectResumeCandidate();
      return;
    }
    if (seed.kind === "resume") {
      this.openForResume(seed.job);
    } else {
      this.openForRetry(seed.job);
    }
  };

  // Embodiment 선택 후보를 한 번 받는다. 실패해도 업로드는 막지 않는다. embodiment 를 지정하지
  // 않으면 서버는 sentinel 로 저장하고 리뷰에서 확정한다. 이것은 정상 경로다.
  private async loadEmbodiments(): Promise<void> {
    const lifecycleGeneration = this.lifecycleGeneration;
    try {
      const all = await listAllEmbodiments();
      if (!this.isActive(lifecycleGeneration)) {
        return;
      }
      this.snapshot = { ...this.snapshot, embodiments: all, embodimentCatalog: "ready" };
      this.emit();
    } catch {
      if (!this.isActive(lifecycleGeneration)) {
        return;
      }
      // 카탈로그 로드 실패는 업로드를 막지 않는다. 선택지는 비지만 폼은 그대로 쓸 수 있다.
      // 드롭다운이 실패를 알리도록 상태만 남긴다.
      this.snapshot = { ...this.snapshot, embodimentCatalog: "error" };
      this.emit();
    }
  }

  // 고를 수 있는 업로드 위치를 한 번 받는다. 실패해도 업로드는 막지 않는다. 위치를 지정하지
  // 않으면 조직 기본값을 쓴다. 이것은 정상 경로다. 이때 폼은 필드를 감춘다.
  private async loadUploadLocations(): Promise<void> {
    const lifecycleGeneration = this.lifecycleGeneration;
    try {
      const locations = await listDatasetUploadLocations();
      if (!this.isActive(lifecycleGeneration)) {
        return;
      }
      this.snapshot = { ...this.snapshot, uploadLocations: locations };
      this.emit();
    } catch {
      // 카탈로그 로드 실패는 무시한다. 위치 선택만 사라지고 폼은 그대로 쓸 수 있다.
    }
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.lifecycleGeneration += 1;
    this.listeners.clear();
  }

  // 취소·실패·충돌한 잡을 재업로드한다. dataset reset 이므로 전체 파일을 다시 전송한다.
  // 사용자가 폴더를 다시 선택하도록 폼을 pre-fill 한다.
  openForRetry = (job: DatasetUploadJob): void => {
    this.setDialog(createRetryDialog(job));
  };

  // PENDING_UPLOAD 잡을 이어받는다. 같은 폴더를 다시 선택하면 probe diff 로 남은 파일만 업로드한다.
  openForResume = (job: DatasetUploadJob): void => {
    this.setDialog(createResumeDialog(job));
  };

  // new 모드 배너의 "이어서 업로드".
  resumeFromCandidate = (): void => {
    const candidate = this.snapshot.dialog.resumeCandidate;
    if (candidate === null || isForeignLiveLease(candidate)) {
      return;
    }
    this.openForResume(candidate);
  };

  handleFolderInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const files = Array.from(event.target.files ?? []);
    if (files.length > 0) {
      this.applyScan(scanFileList(files));
    }
    event.target.value = "";
  };

  handleDropZoneClick = (): void => {
    const dialog = this.snapshot.dialog;
    if (dialog.busy || dialog.scanning) {
      return;
    }
    document.getElementById(this.options.folderInputId)?.click();
  };

  handleDragEnter = (event: DragEvent<HTMLElement>): void => {
    event.preventDefault();
    this.patchDialog({ dragActive: true });
  };

  handleDragOver = (event: DragEvent<HTMLElement>): void => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    this.patchDialog({ dragActive: true });
  };

  handleDragLeave = (event: DragEvent<HTMLElement>): void => {
    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && event.currentTarget.contains(relatedTarget)) {
      return;
    }
    this.patchDialog({ dragActive: false });
  };

  handleDrop = (event: DragEvent<HTMLElement>): void => {
    event.preventDefault();
    const dialog = this.snapshot.dialog;
    if (dialog.busy || dialog.scanning) {
      return;
    }
    void this.scanDrop(event.dataTransfer.items);
  };

  handleDropZoneKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    const dialog = this.snapshot.dialog;
    if (dialog.busy || dialog.scanning) {
      return;
    }
    event.preventDefault();
    document.getElementById(this.options.folderInputId)?.click();
  };

  handleDisplayNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    this.patchForm({ displayName: event.target.value });
  };

  handleReservedSelectionsChange = (next: ReservedSelections): void => {
    // 출처가 open_source 로 바뀌면 진단 체크를 해제한다. 다른 출처로 바뀌면 되돌린다.
    // 서버는 라벨을 보지 않으므로 이것은 pre-fill 일 뿐이다. 사용자는 곧바로 다시 켤 수 있다.
    // 사용자가 출처를 다시 바꾸기 전까지 그 선택을 유지한다.
    const previous = this.snapshot.dialog.form.reservedSelections;
    const wasOpenSource = isOpenSource(previous);
    const isNowOpenSource = isOpenSource(next);
    if (wasOpenSource === isNowOpenSource) {
      this.patchForm({ reservedSelections: next });
      return;
    }
    // 정본 위치가 진단을 지원하지 않는 잡의 재업로드에서는 출처를 바꿔도 선언을 켜지 않는다.
    const targetJob = this.snapshot.dialog.targetJob;
    const diagnosticsSupported = targetJob == null || uploadJobSupportsBandDiagnostics(targetJob);
    this.patchForm({
      reservedSelections: next,
      bandDiagnosticsEnabled: !isNowOpenSource && diagnosticsSupported,
    });
  };

  handleBandDiagnosticsEnabledChange = (bandDiagnosticsEnabled: boolean): void => {
    this.patchForm({ bandDiagnosticsEnabled });
  };

  handleEmbodimentChange = (embodiment: Embodiment | null): void => {
    // embodiment 를 비우면 그 핸드의 tip 도 함께 비운다. tip 은 EE 말단이라 단독으로는 뜻이 없다.
    this.patchForm(embodiment === null ? { embodiment, tip: null } : { embodiment });
  };

  handleTipChange = (tip: Tip | null): void => {
    this.patchForm({ tip });
  };

  handleDataOriginChange = (dataOrigin: DataOrigin): void => {
    this.patchForm({ dataOrigin });
  };

  handleUploadLocationChange = (uploadLocationCode: string): void => {
    this.patchForm({ uploadLocationCode });
  };

  handleSubmit = (event: SyntheticEvent<HTMLFormElement, SubmitEvent>): void => {
    event.preventDefault();
    const mode = this.snapshot.dialog.mode;
    if (mode === "new") {
      this.submitNew();
    } else if (mode === "retry") {
      this.submitRetry();
    } else if (mode === "resume") {
      this.submitResume();
    }
  };

  retryUpload = (): void => {
    this.submitRetry();
  };

  resumeUpload = (): void => {
    this.submitResume();
  };

  // resume 모드의 "업로드 취소"(dataset). 서버 잡을 cancel 하고 새 폼으로 되돌린다.
  cancelTargetUpload = (): void => {
    void this.runCancelTarget();
  };

  private patchForm(value: Partial<UploadFormState>): void {
    const dialog = this.snapshot.dialog;
    if (dialog.busy) {
      return;
    }
    this.setDialog({
      ...dialog,
      form: { ...dialog.form, ...value },
      errorMessage: null,
    });
  }

  private patchDialog(value: Partial<UploadDialogState>): void {
    this.setDialog({ ...this.snapshot.dialog, ...value });
  }

  private async scanDrop(items: DataTransferItemList): Promise<void> {
    const lifecycleGeneration = this.lifecycleGeneration;
    this.patchDialog({ scanning: true, dragActive: false, errorMessage: null });
    try {
      const result = await scanDroppedItems(items);
      if (!this.isActive(lifecycleGeneration)) {
        return;
      }
      if (result.kind === "not_folder") {
        this.patchDialog({
          scanning: false,
          errorMessage: "개별 파일이 아닌 폴더 전체를 끌어다 놓아 주세요.",
        });
        return;
      }
      if (result.kind === "multiple_roots") {
        this.patchDialog({ scanning: false, errorMessage: "폴더 한 개만 끌어다 놓아 주세요." });
        return;
      }
      this.patchDialog({ scanning: false });
      this.applyScan(result.scan);
    } catch (error) {
      if (!this.isActive(lifecycleGeneration)) {
        return;
      }
      this.patchDialog({
        scanning: false,
        errorMessage: this.options.resolveErrorMessage(error),
      });
    }
  }

  private applyScan(scan: FolderScanResult): void {
    const dialog = this.snapshot.dialog;
    if (dialog.busy) {
      return;
    }
    if (scan.files.length === 0) {
      this.patchDialog({
        dragActive: false,
        errorMessage: "선택한 폴더에 업로드할 파일이 없습니다.",
      });
      return;
    }
    // 이름이 비어 있으면 폴더명에서 추론한다. new 모드에서 이름을 입력하지 않았을 때만 추론한다.
    // retry·resume 는 서버 이름을 유지한다.
    const inferredName = scan.rootName ?? "";
    const displayName =
      dialog.mode === "new" && dialog.form.displayName.trim() === "" && inferredName !== ""
        ? inferredName
        : dialog.form.displayName;
    this.setDialog({
      ...dialog,
      form: { ...dialog.form, scan, displayName, assets: analyzeDatasetUploadAssets(scan.files) },
      dragActive: false,
      errorMessage: null,
      resumeDiff: null,
      resumeDiffFailed: false,
      readFailure: null,
      checkingRead: true,
    });
    void this.checkFolderReadability(scan);
    if (dialog.mode === "resume" && dialog.targetJob !== null) {
      void this.computeResumeDiff(dialog.targetJob, scan);
    }
  }

  // 표본 파일을 실제로 열어 본다. 여기서 거르지 않으면 소유자가 다른 폴더는 전송 단계에서
  // network error 로만 나타난다. 그러면 사용자는 원인을 모른 채 재시도만 할 수 있다.
  private async checkFolderReadability(scan: FolderScanResult): Promise<void> {
    const lifecycleGeneration = this.lifecycleGeneration;
    const failure = await probeFolderReadability(scan.files);
    if (!this.isActive(lifecycleGeneration)) {
      return;
    }
    // 확인하는 동안 사용자가 다른 폴더를 고르면 이전 판정을 버린다.
    if (this.snapshot.dialog.form.scan !== scan) {
      return;
    }
    this.patchDialog({ checkingRead: false, readFailure: failure });
  }

  // resume 모드에서 staged 실측(probe)과 path+size 를 대조한다. 그 결과로 스킵·재전송·고아
  // 요약을 미리 보여 준다.
  private async computeResumeDiff(job: DatasetUploadJob, scan: FolderScanResult): Promise<void> {
    const lifecycleGeneration = this.lifecycleGeneration;
    this.patchDialog({
      busy: true,
      resumeProbing: true,
      resumeDiffFailed: false,
      errorMessage: null,
    });
    try {
      const staged = await fetchAllStagedFiles(
        (query) => listDatasetUploadStagedFiles(job.uuid, query),
        100,
      );
      if (!this.isActive(lifecycleGeneration)) {
        return;
      }
      const dialog = this.snapshot.dialog;
      if (dialog.mode !== "resume" || dialog.form.scan !== scan) {
        return;
      }
      const diff = diffStagedFiles(scan.files, staged);
      this.patchDialog({
        busy: false,
        resumeProbing: false,
        resumeDiff: {
          skippedCount: diff.skipped.length,
          skippedBytes: diff.skipped.reduce((sum, file) => sum + file.sizeBytes, 0),
          remainingCount: diff.toUpload.length,
          remainingBytes: diff.toUpload.reduce((sum, file) => sum + file.sizeBytes, 0),
          mismatchedCount: diff.mismatched.length,
          orphanCount: diff.orphans.length,
        },
      });
    } catch {
      if (!this.isActive(lifecycleGeneration)) {
        return;
      }
      // diff 미리보기가 실패해도 재개는 막지 않는다. 엔진이 시작할 때 다시 계산한다. 그래서 오류로
      // 올리지 않고 미리보기 실패만 남긴다.
      this.patchDialog({ busy: false, resumeProbing: false, resumeDiffFailed: true });
    }
  }

  // 창이 열린 직후 내 최근 업로드를 조회한다. 미완료(pending_upload, files 모드) 잡을 찾으면
  // 배너 후보로 설정한다.
  private async detectResumeCandidate(): Promise<void> {
    const lifecycleGeneration = this.lifecycleGeneration;
    try {
      const candidate = await this.findResumeCandidate();
      if (candidate === null || !this.isActive(lifecycleGeneration)) {
        return;
      }
      const dialog = this.snapshot.dialog;
      if (dialog.mode !== "new") {
        return;
      }
      this.patchDialog({ resumeCandidate: candidate });
    } catch {
      // 재개 탐지 실패는 무시한다. 새 업로드 경로는 그대로 쓸 수 있다.
    }
  }

  // 잡 목록이 바뀐 뒤(취소 등) 배너 후보를 다시 맞춘다. 탐지(detectResumeCandidate)와 달리
  // 후보가 없어지면 배너를 지운다. 방금 취소한 잡이 계속 보이면 안 되기 때문이다.
  refreshResumeCandidate = async (): Promise<void> => {
    const lifecycleGeneration = this.lifecycleGeneration;
    try {
      const candidate = await this.findResumeCandidate();
      if (!this.isActive(lifecycleGeneration) || this.snapshot.dialog.mode !== "new") {
        return;
      }
      this.patchDialog({ resumeCandidate: candidate });
    } catch {
      // 다시 조회하다 실패하면 무시한다. 새 업로드 경로는 그대로 쓸 수 있다.
    }
  };

  private async findResumeCandidate(): Promise<DatasetUploadJob | null> {
    const page = await listDatasetUploads({
      page: 1,
      size: resumeScanPageSize,
      scope: "me",
    });
    const candidates = listResumableUploads(page.items);
    return candidates.length > 0 ? candidates[0] : null;
  }

  // 신규 업로드. 검증한 뒤 큐에 넣고 창을 진행 단계로 전환한다.
  private submitNew(): void {
    const dialog = this.snapshot.dialog;
    if (dialog.busy || dialog.mode !== "new") {
      return;
    }
    const scan = dialog.form.scan;
    const displayName = dialog.form.displayName.trim();
    const error = this.validate(dialog.form, displayName);
    if (error !== null || scan === null) {
      this.patchDialog({ errorMessage: error });
      return;
    }
    const spec: UploadJobSpec = {
      kind: "new",
      scan,
      displayName,
      reservedLabels: toReservedLabels(dialog.form.reservedSelections),
      embodimentUuid: dialog.form.embodiment?.uuid ?? null,
      tipUuid: dialog.form.tip?.uuid ?? null,
      dataOrigin: dialog.form.dataOrigin,
      bandDiagnosticsEnabled: dialog.form.bandDiagnosticsEnabled,
      uploadLocationCode: dialog.form.uploadLocationCode,
    };
    this.submitSpec(spec, displayName);
  }

  private submitRetry(): void {
    const dialog = this.snapshot.dialog;
    if (dialog.busy || dialog.mode !== "retry" || dialog.targetJob === null) {
      return;
    }
    const scan = dialog.form.scan;
    // 이름은 서버가 고정하므로 검증에서 제외하고 폴더·레이블·행동 주체만 본다.
    const error = this.validate(dialog.form, dialog.targetJob.display_name);
    if (error !== null || scan === null) {
      this.patchDialog({ errorMessage: error });
      return;
    }
    // 두 필드의 null 은 "비움"으로 보낸다. 폼은 잡의 nested row 로 동기 시드된다. 그래서 여기서
    // 빈 값은 사용자가 비운 값뿐이다. 이 계약은 buildDatasetUploadResetReq 가 소유한다.
    const embodimentUuid = dialog.form.embodiment?.uuid ?? null;
    const tipUuid = dialog.form.tip?.uuid ?? null;
    this.submitSpec(
      {
        kind: "reset",
        scan,
        uploadJob: dialog.targetJob,
        reservedLabels: toReservedLabels(dialog.form.reservedSelections),
        embodimentUuid,
        tipUuid,
        dataOrigin: dialog.form.dataOrigin,
        bandDiagnosticsEnabled: dialog.form.bandDiagnosticsEnabled,
      },
      dialog.targetJob.display_name,
    );
  }

  private submitResume(): void {
    const dialog = this.snapshot.dialog;
    if (dialog.busy || dialog.mode !== "resume" || dialog.targetJob === null) {
      return;
    }
    if (isForeignLiveLease(dialog.targetJob)) {
      this.patchDialog({
        errorMessage:
          "다른 브라우저에서 이 Dataset을 업로드 중입니다. 해당 업로드가 끝나거나 만료된 뒤 다시 시도해 주세요.",
      });
      return;
    }
    const scan = dialog.form.scan;
    if (scan === null) {
      this.patchDialog({ errorMessage: "이어서 올리려면 같은 폴더를 다시 선택해 주세요." });
      return;
    }
    const readBlock = this.folderReadBlock();
    if (readBlock !== null) {
      this.patchDialog({ errorMessage: readBlock });
      return;
    }
    this.submitSpec(
      { kind: "resume", scan, uploadJob: dialog.targetJob },
      dialog.targetJob.display_name,
    );
  }

  private submitSpec(spec: UploadJobSpec, displayName: string): void {
    const engineJobId = this.options.enqueue(spec);
    this.options.onSubmitted(engineJobId, displayName);
  }

  private async runCancelTarget(): Promise<void> {
    const lifecycleGeneration = this.lifecycleGeneration;
    const dialog = this.snapshot.dialog;
    if (dialog.busy || dialog.targetJob === null) {
      return;
    }
    this.patchDialog({ busy: true, errorMessage: null });
    try {
      await cancelDatasetUpload(dialog.targetJob.uuid);
      if (!this.isActive(lifecycleGeneration)) {
        return;
      }
      await this.options.onJobsRefresh?.();
      if (!this.isActive(lifecycleGeneration)) {
        return;
      }
      // 취소한 뒤 새 업로드 폼으로 되돌린다. 창은 유지하므로 사용자가 새로 시작할 수 있다.
      this.setDialog(createNewDialog());
    } catch (error) {
      if (!this.isActive(lifecycleGeneration)) {
        return;
      }
      this.patchDialog({ busy: false, errorMessage: this.options.resolveErrorMessage(error) });
    }
  }

  // 읽지 못하는 폴더로는 시작할 수 없다. 전송을 시작해도 모든 파일이 실패한다.
  // 세 진입 경로가 같은 판정을 쓴다.
  private folderReadBlock(): string | null {
    const dialog = this.snapshot.dialog;
    if (dialog.checkingRead) {
      return "폴더의 파일을 확인하고 있습니다. 잠시 후 다시 시도해 주세요.";
    }
    if (dialog.readFailure !== null) {
      return "선택한 폴더의 파일을 읽을 수 없습니다. 폴더 안내를 확인한 뒤 다시 선택해 주세요.";
    }
    return null;
  }

  // 폴더, 이름, (dataset) 필수 레이블, 행동 주체를 검증한다. 통과하면 null 을 돌려준다.
  private validate(form: UploadFormState, displayName: string): string | null {
    const scan = form.scan;
    if (scan === null || scan.files.length === 0) {
      return "업로드할 Dataset 폴더를 선택해 주세요.";
    }
    const readBlock = this.folderReadBlock();
    if (readBlock !== null) {
      return readBlock;
    }
    if (displayName.trim() === "") {
      return "이름을 입력해 주세요.";
    }
    const missing = findMissingRequiredKeys(this.reservedRequiredKeys, form.reservedSelections);
    if (missing.length > 0) {
      return `다음 필수 라벨을 선택해 주세요: ${missing.map(reservedKeyLabel).join(", ")}.`;
    }
    // 서버는 complete 단계에서만 막는다. 전송을 다 끝낸 뒤 거절되지 않도록 여기서 먼저 막는다.
    if (form.dataOrigin === null) {
      return "행동 주체를 선택해 주세요.";
    }
    return null;
  }

  private isActive(generation: number): boolean {
    return !this.disposed && generation === this.lifecycleGeneration;
  }

  private setDialog(dialog: UploadDialogState): void {
    if (this.disposed) {
      return;
    }
    // 카탈로그는 폼 상태와 수명이 다르다. 그래서 mode 를 바꾸어도 그대로 둔다.
    this.snapshot = { ...this.snapshot, dialog };
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}

function createDialog(mode: UploadDialogMode): UploadDialogState {
  return {
    mode,
    form: { ...emptyForm },
    errorMessage: null,
    busy: false,
    scanning: false,
    dragActive: false,
    resumeCandidate: null,
    targetJob: null,
    resumeDiff: null,
    resumeProbing: false,
    resumeDiffFailed: false,
    readFailure: null,
    checkingRead: false,
  };
}

// 신규 업로드 다이얼로그. 세 드롭다운(환경, 출처, 행동 주체)은 uploadFormDefaults 값으로 미리
// 선택한다. 다른 두 진입(재업로드, 이어받기)은 서버 저장값을 표시하므로 이 기본값을 쓰지 않는다.
function createNewDialog(): UploadDialogState {
  return {
    ...createDialog("new"),
    form: {
      ...emptyForm,
      reservedSelections: { ...UPLOAD_FORM_DEFAULTS.reservedSelections },
      dataOrigin: UPLOAD_FORM_DEFAULTS.dataOrigin,
    },
  };
}

// 재업로드(reset) 다이얼로그(dataset). 이름은 서버 값으로 고정한다. 레이블은 서버 저장값으로
// pre-fill 한다. 사용자는 폴더만 다시 선택한다.
function createRetryDialog(job: DatasetUploadJob): UploadDialogState {
  return {
    ...createDialog("retry"),
    form: {
      ...emptyForm,
      displayName: job.display_name,
      // null 해석(저장하지 않은 레이블, seed 가 정리된 잡)은 api 경계 헬퍼가 소유한다. resume 경로와
      // 같은 규칙을 써야 같은 잡이 진입점에 따라 다른 기본값을 갖지 않는다.
      reservedSelections: prefillReservedLabels(job),
      // 잡의 nested row 를 그대로 채운다(없으면 null). 값이 같으면 reset 이 키를 생략해 서버 값을
      // 유지한다. 사용자가 바꾸거나 비우면 그 상태를 보낸다. 두 필드 모두 **동기 시드**여야 한다.
      // 비동기로 채우면 그 사이에 폼의 빈 값이 "비움"으로 제출되어 서버 값을 지운다.
      embodiment: job.embodiment,
      tip: job.tip,
      // seed Dataset 의 현재 선언을 그대로 채운다. seed 가 정리된 잡이면 null 이므로 사용자가 다시 고른다.
      dataOrigin: job.data_origin,
      bandDiagnosticsEnabled: prefillBandDiagnosticsEnabled(job),
    },
    targetJob: job,
  };
}

// 이어받기(resume) 다이얼로그. 폼 입력은 없다. 순서는 다음과 같다.
//   - 폴더를 다시 선택한다.
//   - diff 를 미리 본다.
//   - 남은 파일을 업로드한다.
// 행동 주체는 서버 값을 그대로 표시한다. 전송이 시작된 뒤 비활성 폼이 빈 값으로 보이지 않게
// 하려는 표시용이다. resume payload 에는 포함하지 않는다. seed Dataset 이 이미 이 값을 가진다.
function createResumeDialog(job: DatasetUploadJob): UploadDialogState {
  return {
    ...createDialog("resume"),
    form: { ...emptyForm, displayName: job.display_name, dataOrigin: job.data_origin },
    targetJob: job,
  };
}

// 출처가 공개 코퍼스로 선택되어 있는지 확인한다. 이 값은 진단 체크의 pre-fill 근거일 뿐이다.
// 실제 판정은 서버가 폼이 보낸 플래그로만 한다.
function isOpenSource(selections: ReservedSelections): boolean {
  return (selections.source ?? []).includes("open_source");
}

function toReservedLabels(selections: ReservedSelections): UploadReservedLabels {
  const result: UploadReservedLabels = {};
  for (const [key, values] of Object.entries(selections)) {
    result[key] = [...values];
  }
  return result;
}
